import { useAlertContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EdictEntryResponse, EdictSelectionRequest, edictsApi } from "@/api";
import { useTranslation } from "@/translations";

import { EdictSearchParams } from "../Edicts.types";
import { edictsQueryKey } from "./useEdictsQuery";

/**
 * Creates the case note and the assignment for the selected edicts. The written notes are merged into the
 * cached result set rather than triggering a new search run: the register call behind it is a live, slow
 * external request, and everything the table has to change is already in the response.
 */
export function useCreateEdictRecords(searchParams: EdictSearchParams | null, onSettled: () => void) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();

  return useMutation({
    mutationFn: (entries: EdictSelectionRequest[]) =>
      edictsApi.createEdictRecords({ createEdictRecordsRequest: { entries } }),
    onSuccess: (written, entries) => {
      if (searchParams !== null) {
        const caseNoteIdsByReference = new Map(written.map((record) => [record.sourceReference, record.caseNoteId]));

        queryClient.setQueryData<EdictEntryResponse[]>(edictsQueryKey(searchParams), (current) =>
          current?.map((entry) => {
            const caseNoteId =
              entry.sourceReference !== null ? caseNoteIdsByReference.get(entry.sourceReference) : undefined;

            return caseNoteId === undefined ? entry : { ...entry, caseNoteId };
          })
        );
      }

      if (written.length === 0) {
        showError(t("edicts.notifications.recordsCreatedNone"));
      } else if (written.length < entries.length) {
        showSuccess(
          t("edicts.notifications.recordsCreatedPartial", { count: written.length, selected: entries.length })
        );
      } else {
        showSuccess(t("edicts.notifications.recordsCreated", { count: written.length }));
      }
    },
    onSettled,
  });
}
